/**
 * components/ui/PasswordStrengthIndicator.tsx
 *
 * Visual indicator of password strength.
 * Evaluates password on 4 levels: Faible / Moyen / Bon / Excellent.
 * Does not render when password is empty.
 */

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface StrengthLevel {
  score: number;
  label: string;
  barColor: string;
  labelColor: string;
}

function evaluateStrength(password: string): StrengthLevel {
  if (password.length === 0) {
    return { score: 0, label: '', barColor: 'bg-neutral-200', labelColor: '' };
  }
  if (password.length < 8) {
    return { score: 1, label: 'Faible', barColor: 'bg-error-500', labelColor: 'text-error-600' };
  }

  const hasUpper = /[A-Z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  const criteriaCount = [hasUpper, hasDigit, hasSpecial].filter(Boolean).length;

  if (password.length >= 10 && criteriaCount === 3) {
    return { score: 4, label: 'Excellent', barColor: 'bg-success-600', labelColor: 'text-success-700' };
  }
  if (criteriaCount >= 2) {
    return { score: 3, label: 'Bon', barColor: 'bg-success-500', labelColor: 'text-success-600' };
  }
  return { score: 2, label: 'Moyen', barColor: 'bg-warning-500', labelColor: 'text-warning-600' };
}

export default function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  if (!password) return null;

  const strength = evaluateStrength(password);

  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
              level <= strength.score ? strength.barColor : 'bg-neutral-200'
            }`}
          />
        ))}
      </div>
      {strength.label && (
        <p className={`text-xs font-medium ${strength.labelColor}`}>{strength.label}</p>
      )}
    </div>
  );
}
